import React, { useState, useEffect } from "react";
import InfoIcon from "assets/icons/information.svg";
import GroupComparison from "./GroupComparison";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { Button } from "components/Buttons";
import moment from "moment";
import Download from "@mui/icons-material/Download";
import { Typography, Box } from "@material-ui/core";
import BarChart from "components/BarChart/BarChart";
import BarChartDouble from "components/BarChart/BarChartDouble";
import AthleteIcon from "assets/icons/athleteIcon.svg";
import { getCoachTeams } from "repositories/teamsApi";
import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import { useAppSelector } from "hooks/reduxHooks";
import PointChart from "components/PointChart/PointChart";
import Tooltip from "@mui/material/Tooltip";

const letters = ["A", "A", "B", "C", "D", "E", "F", "G"];

// Updated interface for reference
const COLORS = [
  "hsl(190, 45%, 75%)", // Soft sky blue
  "hsl(90, 45%, 75%)", // Soft mint
  "hsl(40, 45%, 75%)", // Soft gold
  "hsl(350, 45%, 75%)", // Soft coral
];

const ALT_COLORS = {
  positive: "hsl(145, 50%, 75%)", // Soft success green
  negative: "hsl(0, 50%, 75%)", // Soft error red
};

interface Athlete {
  _id: string;
  firstName: string;
  lastName: string;
  avatar: string;
  rank: string;
  age: number;
  teams: string[];
  positions: string[];
  sports: string[];
  kneeMetric: number;
  lastExam: string;
  calculations: {
    currentAverage: number;
    previousAverage: number;
    averagesDiffPercentage: number;
    hasImproved: boolean;
  };
  leftSideData: {
    current: {
      leftMinValue: number;
    };
    previous: {
      leftMinValue: number;
    };
  };
  rightSideData: {
    current: {
      rightMinValue: number;
    };
    previous: {
      rightMinValue: number;
    };
  };
  bothSideData: {
    current: {
      leftMinValue: number;
      rightMinValue: number;
    };
    previous: {
      leftMinValue: number;
      rightMinValue: number;
    };
  };

  patient: {
    _id: string;
    imageUrl: string;
    profileInfo: {
      firstName: string;
      lastName: string;
      dateOfBirth: string;
      sports: string[];
      teams: string[];
      gender: string;
      sportPosition: string[];
    };
  };
}

interface Group {
  _id: number;
  teams: Array<any>; // or Team[] if you have a Team type
  sports: Array<any>; // or Sport[] if you have a Sport type
  genders: Array<any>; // or Gender[] if you have a Gender type
  positions: Array<any>; // or Position[] if you have a Position type
  average: string;
  previousAverage: string;
  athletes: {
    calculations?: any;
    patient: {
      profileInfo: {
        firstName: string;
        lastName: string;
        teams: string[];
      };
    };
  }[];
}

const PrintWrapper = ({ children }: { children: any }) => {
  useEffect(() => {
    // Create a style element
    const style = document.createElement("style");

    // Add print-specific CSS
    style.textContent = `
      @page {
        size: landscape;
      }
      
      @media print {
        body {
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact;
        }
      }
    `;

    // Add style to document head
    document.head.appendChild(style);

    // Cleanup
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return <div className="print-container">{children}</div>;
};

const SideBySidePage: React.FC = () => {
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const currentClinician: any = useAppSelector(selectCurrentClinician);
  const [groups, setGroups] = useState<Group[]>([
    {
      _id: 1,
      teams: [],
      sports: [],
      genders: [],
      positions: [],
      average: "",
      previousAverage: "",
      athletes: [],
    },
  ]);

  const getTeams = async () => {
    if (!currentClinician) return;
    try {
      const fetchedTeams = await getCoachTeams(currentClinician._id);

      setTeams(fetchedTeams);
    } catch (error) {}
  };

  const shouldShowPointChart = () => {
    const data = getPointChartData();

    return data.some((group) => group.data.length > 0);
  };
  const getPointChartData = () => {
    const data = groups.map((group, index) => {
      return {
        id: `Group ${letters[group._id]}`,
        data: group.athletes
          .map((athlete) => {
            return {
              x: index + 1,
              y: athlete.calculations.currentAverage,
            };
          })
          .filter((item) => item.y),
      };
    });
    return data;
  };

  useEffect(() => {
    getTeams();
  }, []);
  console.log({ groups });
  return (
    <div className="p-3 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl flex items-baseline gap-2">
          <span
            className="font-black"
            style={{
              fontFamily: "Sofia Pro",
            }}
          >
            Report overview
          </span>
          <span className="font-light text-gray-700">- Comparative list</span>
          <span className="font-light text-gray-500">
            | {moment().format("MM/DD/YY")}
          </span>
        </h1>

        <Button
          style={{ maxHeight: "40px", marginTop: "20px" }}
          title={"Create New Report"}
          onClick={() => {}}
          type={ButtonType.normal}
          colorScheme={ButtonColorScheme.gradient}
        />
      </div>

      <hr className="mb-5" />

      <div className="mb-6">
        <p
          className="text-gray-600"
          style={{ display: "flex", alignItems: "center", gap: "5px" }}
        >
          Report Type:{" "}
          <span
            style={{
              width: "200px",
              padding: "5px",
              borderRadius: "15px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#000",
              color: "#fff",
            }}
          >
            Side-by-side analysis
          </span>
        </p>
      </div>

      <div className="flex items-center gap-2 ">
        <h2 className="text-xl font-bold">Athlete list</h2>
      </div>
      <GroupComparison groups={groups} setGroups={setGroups} teams={teams} />

      <>
        <Typography
          variant="h6"
          style={{
            fontFamily: "Sofia Pro",
            flexWrap: "nowrap",
            display: "flex",
            fontWeight: "600",
            marginTop: "20px",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          Number of athletes per group
          <Tooltip
            placement="top"
            title="This chart shows the number of athletes in each group."
          >
            <img
              src={InfoIcon}
              alt="filterTable"
              style={{ width: "25px", height: "25px", cursor: "pointer" }}
              className={`ml-2 mt-1 w-[15px] h-[15px] object-fill fill-black`}
            />
          </Tooltip>
        </Typography>
        <div
          style={{
            width: "100%",
            display: "flex",
          }}
        >
          {groups.map((group, index) => {
            return (
              <Box
                bgcolor={"#f0f0f0d5"}
                sx={{
                  flexDirection: "column",
                  padding: "75px",
                  maxHeight: "300px",
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  marginRight: "20px",
                }}
              >
                <img
                  aria-hidden="true"
                  src={AthleteIcon}
                  alt="close"
                  style={{ cursor: "pointer", width: "75px", height: "75px" }}
                />
                <Typography
                  style={{
                    marginTop: "10px",
                    marginBottom: "5px",
                    fontFamily: "Sofia Pro",
                    color: "#5C5D69",
                  }}
                  variant="h5"
                >
                  Group {letters[index + 1]}
                </Typography>
                <Typography
                  variant="h4"
                  style={{
                    fontFamily: "Sofia Pro",
                  }}
                >
                  {group.athletes?.length}
                </Typography>
              </Box>
            );
          })}
        </div>
      </>

      <Typography
        variant="h6"
        style={{
          fontFamily: "Sofia Pro",
          flexWrap: "nowrap",
          display: "flex",
          fontWeight: "600",
          marginTop: "20px",
          alignItems: "center",
        }}
      >
        Comparison of groups averages
        <Tooltip
          placement="top"
          title="This chart shows the average of each group."
        >
          <img
            src={InfoIcon}
            alt="filterTable"
            style={{ width: "25px", height: "25px", cursor: "pointer" }}
            className={`ml-2 mt-1 w-[15px] h-[15px] object-fill fill-black`}
          />
        </Tooltip>
      </Typography>
      <BarChart
        data={groups.map((item, index) => {
          return {
            ...item,
            group: `Group ${letters[item._id]}`,
            averageColor: COLORS[index],
          };
        })}
      />
      <Typography
        variant="h6"
        style={{
          fontFamily: "Sofia Pro",
          flexWrap: "nowrap",
          display: "flex",
          fontWeight: "600",
          marginTop: "20px",
          alignItems: "center",
        }}
      >
        Improvement per group
        <Tooltip
          placement="top"
          title="This chart shows the improvement of each group."
        >
          <img
            src={InfoIcon}
            alt="filterTable"
            style={{ width: "25px", height: "25px", cursor: "pointer" }}
            className={`ml-2 mt-1 w-[15px] h-[15px] object-fill fill-black`}
          />
        </Tooltip>
      </Typography>
      <BarChartDouble
        data={groups.map((item, index) => {
          return {
            ...item,
            group: `Group ${letters[item._id]}`,
            previousAverageColor: "hsl(30, 100%, 58%)",
            previousAverage: item.previousAverage,
            averageColor:
              Number(item.previousAverage) < Number(item.average)
                ? ALT_COLORS.positive
                : ALT_COLORS.negative,
          };
        })}
      />
      <hr style={{ marginTop: "10px", marginBottom: "10px" }} />
      <Typography
        variant="h6"
        style={{
          fontFamily: "Sofia Pro",
          flexWrap: "nowrap",
          display: "flex",
          fontWeight: "600",
          marginTop: "20px",
          alignItems: "center",
        }}
      >
        Athlete average of each group
        <Tooltip
          placement="top"
          title="This chart shows the average of each athlete in each group."
        >
          <img
            src={InfoIcon}
            alt="filterTable"
            style={{ width: "25px", height: "25px", cursor: "pointer" }}
            className={`ml-2 mt-1 w-[15px] h-[15px] object-fill fill-black`}
          />
        </Tooltip>
      </Typography>
      {shouldShowPointChart() && <PointChart data={getPointChartData()} />}

      <div
        style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
      >
        <PrintWrapper>
          <button
            onClick={() => {
              window.print();
            }}
            className="flex items-center gap-2 bg-white text-gray-600 px-6 py-3 rounded-full shadow-lg hover:bg-gray-50 transition-colors duration-200 print:hidden"
          >
            <Download />
            <span className="text-lg">Download PDF</span>
          </button>
        </PrintWrapper>
      </div>
    </div>
  );
};

export default SideBySidePage;
